/**
 * Centralized money rounding rule for Family Loan Planner™.
 *
 * ONE rule, used by the Canadian and U.S. engines and by reports:
 *
 *  1. All displayed / persisted monetary values are rounded HALF-UP to 2 dp
 *     (banker's rounding is intentionally NOT used — carrier statements and
 *     amortization schedules conventionally use half-up on the cent).
 *  2. A value is only treated as zero when its absolute value is strictly
 *     LESS THAN ONE CENT (< $0.005 after rounding, i.e. rounds to $0.00).
 *     A residual of $0.01–$0.09 is a REAL balance and must never be shown
 *     as paid off.
 *  3. Internal iteration/termination uses EPSILON (1e-6) so floating point
 *     dust does not create phantom payments; it must never be used for
 *     display.
 *
 * See docs/calculation-validation.md for the full documented assumptions.
 */

/** Floating-point dust tolerance for loop termination only. Never for display. */
export const EPSILON = 1e-6;

/** One cent. The smallest representable money amount. */
export const ONE_CENT = 0.01;

/** Round half-up to 2 decimal places. */
export function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const scaled = value * 100;
  // Guard against binary representation error (e.g. 1.005 * 100 = 100.49999…)
  const corrected = Math.round((scaled + Number.EPSILON * Math.sign(scaled || 1) * 100) * 1e6) / 1e6;
  return Math.sign(corrected) * Math.round(Math.abs(corrected)) / 100;
}

/**
 * Display/persistence normalisation: round to the cent and collapse to exactly
 * 0 ONLY when the rounded amount is below one cent.
 * A $0.01 residual survives; $0.004 does not.
 */
export function snapDisplayZero(value: number): number {
  const rounded = roundMoney(value);
  return Math.abs(rounded) < ONE_CENT ? 0 : rounded;
}

/** True when a balance is genuinely settled (rounds to less than one cent). */
export function isPaidOff(balance: number): boolean {
  return Math.abs(roundMoney(balance)) < ONE_CENT;
}